/* ---------------------------------------------------------------------------------------------------------------------
    CUSTOM DELEGATE EXTENSION
   ------------------------------------------------------------------------------------------------------------------ */
HTMLElement.prototype.customDelegate = function customDelegate(
  eventName,
  elementSelector,
  handler
) {
  this.addEventListener(
    eventName,
    (e) => {
      const element = e.target.closest(elementSelector);

      if (element) {
        handler.call(element, e, element);
      }
    },
    false
  );
};

HTMLElement.prototype.removeCustomDelegate = function removeCustomDelegate(
  eventName,
  elementSelector,
  handler
) {
  this.removeEventListener(
    eventName,
    (e) => {
      const element = e.target.closest(elementSelector);

      if (element) {
        handler.call(element, e, element);
      }
    },
    false
  );
};

/* ---------------------------------------------------------------------------------------------------------------------
    DEBOUNCE EXTENSION
   ------------------------------------------------------------------------------------------------------------------ */
Function.prototype.debounce = function (delay) {
  let timeoutId;
  const func = this;

  return function (...args) {
    clearTimeout(timeoutId);

    timeoutId = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };
};

/* ---------------------------------------------------------------------------------------------------------------------
    FOCUS TRAP EXTENSION
   ------------------------------------------------------------------------------------------------------------------ */
HTMLElement.prototype.getFocusableElements = function () {
  return getFocusableElements(this);
};

HTMLElement.prototype.trapFocus = function () {
  this.removeTrapFocus();

  const container = this;

  this._focusTrapHandler = function (event) {
    if (event.key !== "Tab") return;

    const focusableElements = container.getFocusableElements();
    const activeElement = document.activeElement;

    if (!focusableElements.length) {
      event.preventDefault();
      container.focus();
      return;
    }

    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements[focusableElements.length - 1];
    const focusIsOutside = !container.contains(activeElement);

    if (event.shiftKey && (activeElement === firstFocusableElement || activeElement === container || focusIsOutside)) {
      event.preventDefault();
      lastFocusableElement.focus();
    } else if (!event.shiftKey && (activeElement === lastFocusableElement || activeElement === container || focusIsOutside)) {
      event.preventDefault();
      firstFocusableElement.focus();
    }
  };

  this._focusTrapDocumentHandler = function (event) {
    if (event.key !== "Tab" || container.contains(document.activeElement)) return;

    event.preventDefault();
    container._focusTrapHandler(event);
  };

  this.addEventListener("keydown", this._focusTrapHandler);
  document.addEventListener("keydown", this._focusTrapDocumentHandler, true);
};

HTMLElement.prototype.removeTrapFocus = function () {
  if (this._focusTrapHandler) {
    this.removeEventListener("keydown", this._focusTrapHandler);
    this._focusTrapHandler = null;
  }

  if (this._focusTrapDocumentHandler) {
    document.removeEventListener("keydown", this._focusTrapDocumentHandler, true);
    this._focusTrapDocumentHandler = null;
  }
};

HTMLElement.prototype.inertBackground = function () {
  this.restoreBackground();

  if (isThemeDesignMode()) return;

  this._backgroundInerted = [];

  let current = this;

  while (current && current !== document.body) {
    const parent = current.parentElement;

    if (!parent) break;

    [...parent.children].forEach((sibling) => {
      if (sibling === current) return;
      if (sibling.matches("script, style, link, template, noscript")) return;
      if (sibling.hasAttribute("inert")) return;

      sibling.setAttribute("inert", "");
      sibling.setAttribute("data-focus-contained", "");
      this._backgroundInerted.push(sibling);
    });

    current = parent;
  }
};

HTMLElement.prototype.restoreBackground = function () {
  (this._backgroundInerted || []).forEach((element) => {
    element.removeAttribute("inert");
    element.removeAttribute("data-focus-contained");
  });

  this._backgroundInerted = [];
};

/* ---------------------------------------------------------------------------------------------------------------------
    WAIT FOR ELEMENT EXTENSION
   ------------------------------------------------------------------------------------------------------------------ */
HTMLElement.prototype.waitForElementVisibility = function () {
  return new Promise((resolve, reject) => {
    const observer = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          resolve();
          observer.unobserve(entry.target);
        }
      });
    });

    observer.observe(this);
  });
};

/* -----------------------------
    CACHED FETCH
   ----------------------------- */

window.cachedResponses = new Map();

window.cachedFetch = async function (url, immediate = true, delay = 500) {
  if (cachedResponses.has(url)) {
    if (!immediate) {
      await new Promise(resolve => setTimeout(resolve, delay));
    }

    return Promise.resolve(cachedResponses.get(url));
  }

  const networkResponse = await fetch(url);

  if (networkResponse.ok) {
    const contentType = networkResponse.headers.get("content-type");

    if (contentType.includes("application/json")) {
      const responseBody = await networkResponse.json();
      cachedResponses.set(url, responseBody);

      return Promise.resolve(responseBody);
    } else if (contentType.includes("text")) {
      const textResponse = await networkResponse.text();
      cachedResponses.set(url, textResponse);

      return Promise.resolve(textResponse);
    } else {
      const blobResponse = await networkResponse.blob();
      return Promise.resolve(blobResponse);
    }
  }

  return Promise.resolve(networkResponse);
}

/* ---------------------------------------------------------------------------------------------------------------------
    REDUCED MOTION
   ------------------------------------------------------------------------------------------------------------------ */
function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function isThemeDesignMode() {
  return Boolean(window.Shopify && window.Shopify.designMode);
}

/* ---------------------------------------------------------------------------------------------------------------------
    FOCUSABLE ELEMENTS
   ------------------------------------------------------------------------------------------------------------------ */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[role="button"]:not([disabled])',
  '[role="checkbox"]',
  '[role="link"]',
  '[role="radio"]',
  '[role="textbox"]',
  '[contenteditable="true"]'
].join(", ");

/**
 * Check if a single element itself matches the focusable selector.
 * @param {HTMLElement} element
 * @returns {boolean}
 */
function isFocusable(element) {
  return element.matches(FOCUSABLE_SELECTOR);
}

function isTabbable(element, container) {
  if (element.disabled || element.closest("[hidden]")) return false;

  const inertAncestor = element.closest("[inert]");

  if (inertAncestor && inertAncestor !== container && container.contains(inertAncestor)) return false;

  let node = element;

  while (node && node !== container.parentElement) {
    const style = window.getComputedStyle(node);

    if (style.display === "none" || style.visibility === "hidden") return false;

    node = node.parentElement;
  }

  return true;
}

/**
 * Get all focusable elements within a container.
 * @param {HTMLElement} container
 * @returns {HTMLElement[]}
 */
function getFocusableElements(container) {
  return [...new Set(container.querySelectorAll(FOCUSABLE_SELECTOR))].filter((element) => isTabbable(element, container));
}